import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { MessageSquare, ShoppingBag, Users, DollarSign, ArrowRight } from 'lucide-react';

export default function DashboardPage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(() => {
    api.get('/admin/stats').then(({ data }) => setStats(data)).finally(() => setLoading(false));
  }, [api]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const CARD_LINKS = {
    inquiries: '/admin/inquiries',
    orders: '/admin/orders',
    waitlist: '/admin/waitlist',
  };

  const cards = stats ? [
    { key: 'inquiries', label: 'New Inquiries', value: stats.inquiries.new, total: stats.inquiries.total, icon: MessageSquare, color: 'bg-blue-50 text-blue-600' },
    { key: 'orders', label: 'Pending Orders', value: stats.orders.pending, total: stats.orders.total, icon: ShoppingBag, color: 'bg-amber-50 text-amber-600' },
    { key: 'revenue', label: 'Revenue', value: `$${stats.orders.revenue.toFixed(2)}`, icon: DollarSign, color: 'bg-green-50 text-green-600' },
    { key: 'waitlist', label: 'Waitlist', value: stats.waitlist.total, icon: Users, color: 'bg-purple-50 text-purple-600' },
  ] : [];

  return (
    <AdminLayout>
      <div data-testid="admin-dashboard">
        <h1 className="text-2xl font-medium mb-1" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Dashboard</h1>
        <p className="text-sm text-gray-500 mb-8">Overview of your platform</p>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-[#C4A484] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
              {cards.map((card) => {
                const Icon = card.icon;
                const link = CARD_LINKS[card.key];
                const Wrapper = link ? 'button' : 'div';
                return (
                  <Wrapper
                    key={card.key}
                    onClick={link ? () => navigate(link) : undefined}
                    className={`bg-white rounded-xl border border-gray-100 p-5 text-left ${link ? 'cursor-pointer hover:border-[#C4A484]/40 hover:shadow-sm transition-all group' : ''}`}
                    data-testid={`stat-${card.label.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-xs uppercase tracking-wider text-gray-400">{card.label}</span>
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${card.color}`}>
                        <Icon className="w-4 h-4" />
                      </div>
                    </div>
                    <p className="text-2xl font-semibold">{card.value}</p>
                    <div className="flex items-center justify-between mt-1">
                      {card.total !== undefined && (
                        <p className="text-xs text-gray-400">{card.total} total</p>
                      )}
                      {link && (
                        <span className="text-xs text-[#C4A484] opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
                          View <ArrowRight className="w-3 h-3" />
                        </span>
                      )}
                    </div>
                  </Wrapper>
                );
              })}
            </div>

            {stats && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="bg-white rounded-xl border border-gray-100 p-5">
                  <h3 className="text-sm font-medium mb-4">Inquiries by Type</h3>
                  <div className="space-y-3">
                    {Object.entries(stats.inquiries.by_type).map(([type, count]) => (
                      <div key={type} className="flex items-center justify-between">
                        <span className="text-sm text-gray-600 capitalize">{type.replace('_', ' ')}</span>
                        <span className="text-sm font-medium bg-gray-100 px-3 py-1 rounded-full">{count}</span>
                      </div>
                    ))}
                    {Object.keys(stats.inquiries.by_type).length === 0 && (
                      <p className="text-sm text-gray-400">No inquiries yet</p>
                    )}
                  </div>
                </div>
                <div className="bg-white rounded-xl border border-gray-100 p-5">
                  <h3 className="text-sm font-medium mb-4">Inquiries by Status</h3>
                  <div className="space-y-3">
                    {Object.entries(stats.inquiries.by_status).map(([status, count]) => (
                      <div key={status} className="flex items-center justify-between">
                        <span className="text-sm text-gray-600 capitalize">{status}</span>
                        <span className="text-sm font-medium bg-gray-100 px-3 py-1 rounded-full">{count}</span>
                      </div>
                    ))}
                    {Object.keys(stats.inquiries.by_status).length === 0 && (
                      <p className="text-sm text-gray-400">No inquiries yet</p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </AdminLayout>
  );
}
